/**
 * Ended-event attendance verification + post-event feedback (guest side). Moved out of
 * EventApplyPage in the 2026-09 redesign and restyled on the surface kit; reached from the
 * rail's CONFIRM I WENT + RATE (`#post-event-feedback`) and SOMETHING WENT WRONG
 * (`#post-event-verify`). The `?rate=1` deep link scrolls to the feedback block.
 */
import React, { useState, useEffect } from 'react';
import { Panel, PanelHeader, PanelBody, SurfaceButton, Callout, GREEN, MUTED, DANGER } from '../surface';
import eventService from '../../services/eventService';
import feedbackService from '../../services/feedbackService';

const REASONS = [
  ['attended', 'I attended this event'],
  ['no_show_host', "The host didn't show up"],
  ['event_different', 'Event was significantly different than described'],
  ['other', 'Other issue'],
];

export default function VerificationFeedbackPanel({ event, userId, mobile = false, initialReason = '' }) {
  const [verificationReason, setVerificationReason] = useState(initialReason);
  const [verificationDetails, setVerificationDetails] = useState('');
  const [hasVerified, setHasVerified] = useState(false);
  const [verificationLoading, setVerificationLoading] = useState(false);
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [wouldAttendAgain, setWouldAttendAgain] = useState(null);
  const [feedbackComment, setFeedbackComment] = useState('');
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { if (initialReason) setVerificationReason(initialReason); }, [initialReason]);

  useEffect(() => {
    let cancelled = false;
    const loadData = async () => {
      if (!event?.id || !userId) return;
      const vRes = await eventService.getVerifications(event.id);
      if (!cancelled && vRes.success && vRes.data?.length > 0) setHasVerified(true);
      const fRes = await feedbackService.getEventFeedback(event.id);
      if (!cancelled && fRes.success && fRes.data?.length > 0) setFeedbackSubmitted(true);
    };
    loadData();
    return () => { cancelled = true; };
  }, [event?.id, userId]);

  const escrowReleaseAt = event?.escrowReleaseAt ? new Date(event.escrowReleaseAt) : null;
  const daysLeft = escrowReleaseAt ? Math.max(0, Math.ceil((escrowReleaseAt - new Date()) / (1000 * 60 * 60 * 24))) : 0;

  const handleVerify = async () => {
    if (!verificationReason) return;
    setVerificationLoading(true);
    setError('');
    const res = await eventService.verifyEvent(event.id, { type: 'guest_report', reason: verificationReason, details: verificationDetails || null });
    setVerificationLoading(false);
    if (res.success) setHasVerified(true);
    else setError(res.error?.message || res.error || 'Failed to submit');
  };

  const handleFeedback = async () => {
    setFeedbackLoading(true);
    setError('');
    const res = await feedbackService.submitFeedback(event.id, { wouldAttendAgain, comment: feedbackComment || null });
    setFeedbackLoading(false);
    if (res.success) setFeedbackSubmitted(true);
    else setError(res.error?.message || res.error || 'Failed to submit');
  };

  const field = 'w-full border-2 border-black bg-white font-mono text-black placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-black';

  return (
    <div className={`flex flex-col ${mobile ? 'gap-3' : 'gap-4'}`} data-testid="verification-feedback">
      {escrowReleaseAt && event?.verificationStatus !== 'resolved' && (
        <Callout tone="warning" title="VERIFICATION WINDOW" mobile={mobile}>
          {daysLeft > 0 ? `${daysLeft} day${daysLeft !== 1 ? 's' : ''} remaining to verify attendance.` : 'Verification window closing soon.'}
        </Callout>
      )}

      <Panel id="post-event-verify" data-testid="post-event-verify">
        <PanelHeader label="HOW WAS IT" right={hasVerified ? 'VERIFIED' : 'TAKES 20 SECONDS'} dense={mobile} />
        {!hasVerified && event?.verificationStatus !== 'resolved' ? (
          <PanelBody dense={mobile}>
            <p className="font-mono" style={{ fontSize: 12, color: MUTED, margin: 0, lineHeight: 1.6 }}>Confirm you went — or tell us what went wrong. Your answer is what makes a host escrow-verified.</p>
            <select value={verificationReason} onChange={(e) => setVerificationReason(e.target.value)} className={field} style={{ padding: '10px 12px', fontSize: 13 }} aria-label="What happened">
              <option value="">Select…</option>
              {REASONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <textarea value={verificationDetails} onChange={(e) => setVerificationDetails(e.target.value)} placeholder="Additional details (optional)" className={field} style={{ padding: '10px 12px', fontSize: 13, resize: 'none' }} rows={2} />
            <SurfaceButton variant="fill" size="cta" fullWidth onClick={handleVerify} disabled={!verificationReason || verificationLoading} data-testid="submit-verification">
              {verificationLoading ? 'SUBMITTING…' : verificationReason && verificationReason !== 'attended' ? 'REPORT IT' : 'CONFIRM I WENT'}
            </SurfaceButton>
          </PanelBody>
        ) : (
          <PanelBody dense={mobile}>
            <span className="font-mono font-bold uppercase" style={{ fontSize: 12, letterSpacing: '0.1em', color: GREEN }}>{hasVerified ? 'VERIFICATION SUBMITTED' : 'VERIFICATION CLOSED'}</span>
          </PanelBody>
        )}
      </Panel>

      <Panel id="post-event-feedback" data-testid="post-event-feedback">
        <PanelHeader label="RATE THE HOST" right={feedbackSubmitted ? 'RATED' : 'PUBLIC · YOUR NAME IS NOT'} dense={mobile} />
        {!feedbackSubmitted ? (
          <PanelBody dense={mobile}>
            <p className="font-mono" style={{ fontSize: 12, color: MUTED, margin: 0 }}>Would you go to an event by this host again?</p>
            <div className="flex gap-2">
              <SurfaceButton variant={wouldAttendAgain === true ? 'fill' : 'outline'} size="md" className="flex-1" onClick={() => setWouldAttendAgain(true)} style={wouldAttendAgain === true ? { background: GREEN, borderColor: GREEN } : undefined}>YES</SurfaceButton>
              <SurfaceButton variant={wouldAttendAgain === false ? 'fill' : 'outline'} size="md" className="flex-1" onClick={() => setWouldAttendAgain(false)} style={wouldAttendAgain === false ? { background: DANGER, borderColor: DANGER } : undefined}>NO</SurfaceButton>
            </div>
            <textarea value={feedbackComment} onChange={(e) => setFeedbackComment(e.target.value)} placeholder="Any comments? (optional)" className={field} style={{ padding: '10px 12px', fontSize: 13, resize: 'none' }} rows={2} />
            <SurfaceButton variant="fill" size="cta" fullWidth onClick={handleFeedback} disabled={wouldAttendAgain === null || feedbackLoading} data-testid="submit-feedback">
              {feedbackLoading ? 'SUBMITTING…' : 'SUBMIT FEEDBACK'}
            </SurfaceButton>
          </PanelBody>
        ) : (
          <PanelBody dense={mobile}>
            <span className="font-mono font-bold uppercase" style={{ fontSize: 12, letterSpacing: '0.1em', color: GREEN }}>FEEDBACK SUBMITTED</span>
          </PanelBody>
        )}
      </Panel>

      {error && <p className="font-mono" style={{ fontSize: 12, color: DANGER, margin: 0 }} role="alert">{error}</p>}
    </div>
  );
}
